import { useEffect, useMemo, useState } from 'react';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { useGetCompanyByIdQuery } from 'hooks/useGetCompanyByIdQuery';
import { mapCrmContactToContact } from 'services/company-and-contacts/mappers';
import { toCompanyId } from 'services/entities/MigrationEntities';
import type { Contact } from 'services/entities/PersonEntity';
import { FeatureFlags } from 'utils/featureFlags';
import { useCrmContactsByCompanyId } from 'pages/flex-data/flex-data-links/useCrmContactsByCompanyId';
import { useSearchCrmContacts } from 'pages/flex-data/flex-data-links/useSearchCrmContacts';
import type { CompositeEntityId } from 'services/company-and-contacts/mappers';

export const MIN_CONTACT_SEARCH_TERM_LENGTH = 3;

export function useContactOptions({ companyId, searchTerm }: { companyId?: string | CompositeEntityId; searchTerm?: string } = {}): {
  contacts: Contact[];
  isLoading: boolean;
  refetch: () => void;
} {
  const featureFlags = useFeatureFlag([FeatureFlags.useVariablesBff, FeatureFlags.crmDealLinking]);
  const isUseVariablesBffFlagEnabled = featureFlags[FeatureFlags.useVariablesBff];
  const isCrmDealLinkingEnabled = isUseVariablesBffFlagEnabled && featureFlags[FeatureFlags.crmDealLinking];

  const hasCompany = !!companyId;
  const isSearchTermUsable = !searchTerm || searchTerm.length >= MIN_CONTACT_SEARCH_TERM_LENGTH;

  const {
    data: companyData,
    isLoading: isLegacyLoading,
    refetch: refetchCompany,
  } = useGetCompanyByIdQuery(!isCrmDealLinkingEnabled ? toCompanyId(companyId) : null);

  const [legacyContacts, setLegacyContacts] = useState<Contact[]>(companyData?.people ?? []);

  useEffect(() => {
    if (isCrmDealLinkingEnabled) return;
    const newContacts = companyData?.people ?? [];
    const contactsAreSimilar =
      legacyContacts.length === newContacts.length && legacyContacts.every((contact, index) => contact['id'] == newContacts[index]['id']);
    if (!contactsAreSimilar) {
      setLegacyContacts(newContacts);
    }
  }, [companyData?.people, isCrmDealLinkingEnabled]);

  const crmContactsByCompany = useCrmContactsByCompanyId({
    companyId: companyId as CompositeEntityId,
    enabled: isCrmDealLinkingEnabled && hasCompany,
  });
  const crmContactsSearch = useSearchCrmContacts({
    searchTerm,
    enabled: isCrmDealLinkingEnabled && !hasCompany && isSearchTermUsable,
  });
  const {
    contacts: crmContactsData,
    isLoading: crmContactIsLoading,
    refetch: refetchCrmContacts,
  } = hasCompany ? crmContactsByCompany : crmContactsSearch;

  const crmContacts = useMemo(
    () => crmContactsData.map(mapCrmContactToContact).sort((a, b) => a.fullName.localeCompare(b.fullName)),
    [crmContactsData]
  );

  return {
    contacts: isCrmDealLinkingEnabled ? crmContacts : legacyContacts,
    isLoading: isCrmDealLinkingEnabled ? crmContactIsLoading : isLegacyLoading,
    refetch: isCrmDealLinkingEnabled ? refetchCrmContacts : refetchCompany,
  };
}
